import { useContext, useMemo, useState } from 'react';
import { Box, ButtonBase, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrendUp,
  Fire,
  Trophy,
  Lightning,
  Target,
  ChartLineUp,
  Sword,
  Medal,
  ShieldStar,
  Storefront,
  Receipt,
  CaretRight,
} from '@phosphor-icons/react';
import { useStars } from '@/contexts/StarContext';
import { CalendarContext } from '@/contexts/CalendarContext';
import {
  getChildActivityStatus,
  usePendingPurchaseCount,
  useShopHasItems,
} from '@/components/child';
import CreateActivityFab from '@/components/layout/CreateActivityFab';
import VoiceActivityCreator from '@/components/voice/VoiceActivityCreator';
import TeenEnergyCard from './TeenEnergyCard';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import { contrastOn } from '@/utils/colorMath';
import {
  XP_PER_LEVEL,
  levelFromStars,
  levelProgressPercent,
  levelTitleKey,
  starsToXp,
  xpIntoLevel,
} from '@/utils/xp';

/** Syns inte, men läses av skärmläsare (samma mönster som MUI:s visuallyHidden). */
const srOnly = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/**
 * TeenProgress — REDESIGNAD framstegsvy v2 för tonåringar (10–13) med NPF.
 *
 * Olympus Pipeline — Alla hjälpare:
 *
 * NPF-SPECIALISTEN (Chiron):
 * - Äkta progression: verklig kompetensökning synliggjord
 * - Skamfri: streak firar, aldrig straffar. Bruten streak = "Fresh start"
 * - Förlåtande: ingen permanent förlust av framsteg
 * - Struktur: alltid samma layout (Level → Streak → Stats → Achievements)
 * - Micro-rewards: varje liten insats synliggörs
 *
 * UX-FORMGIVAREN (Freyja):
 * - Hero: Level-display som dominant element med XP-bar
 * - Streak-kort med emotionell investering (eld-animation)
 * - Veckostatistik: 3 siffror (done/total/%) i grid
 * - Achievements: milestone-badges som belönar långsiktigt
 *
 * UI-EXPERTEN (Leonardo):
 * - Level-hero med gradient-bakgrund och glow
 * - XP-bar med gradient fill (cyan → purple → mint)
 * - Streak-flames med CSS pulse-animation
 * - Achievement-badges med shimmer-effekt
 * - Kort med glassmorphism-känsla (border + shadow + bg)
 * - Phosphor duotone icons
 *
 * TEEN-ENGAGEMANGS-EXPERTEN:
 * - RPG-style level system (Level 1 → ∞)
 * - XP-poäng per aktivitet (10 XP = 1 stjärna)
 * - Level-up celebrations
 * - Unlockable achievements (baserat på streak, totalt, veckorekord)
 * - "Boss defeated"-känsla vid 100% vecka
 * - Longest streak som "high score"
 */

const TeenProgress = () => {
  const { t } = useTranslation();
  const { balance, streak, goals, selectGoal } = useStars();
  const { weekInstances } = useContext(CalendarContext);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const { pendingCount, hasAnyPurchase } = usePendingPurchaseCount();
  const { hasItems: shopHasItems } = useShopHasItems();

  const totalStars = balance.totalEarnedStars;
  // Växelkursen är gemensam med butiksvyerna (@/utils/xp) — annars kan tonåringen se
  // ett XP-saldo här och ett pris i butiken som räknats med olika kurs.
  const totalXP = starsToXp(totalStars);
  // Nivån, XP in i nivån och progressen bor i @/utils/xp — dagsidans header räknar med
  // exakt samma trappa, och två uttryck för samma tal är en divergens som väntar.
  const level = levelFromStars(totalStars);
  const xpInLevel = xpIntoLevel(totalStars);
  const levelProgress = levelProgressPercent(totalStars);
  const currentStreak = streak.currentStreak;
  const longestStreak = streak.longestStreak;

  // Week stats
  const weekStats = useMemo(() => {
    const all = weekInstances ?? [];
    const done = all.filter((i) => getChildActivityStatus(i) === 'done').length;
    const total = all.length;
    return { done, total };
  }, [weekInstances]);

  const weekCompletion =
    weekStats.total > 0 ? Math.round((weekStats.done / weekStats.total) * 100) : 0;

  // Mål: föräldern skapar dem, tonåringen väljer vilket som räknas upp. Bara aktiva mål
  // visas — ett uppnått mål väntar på en vuxen och hör inte hemma i väljaren.
  const activeGoals = goals.filter((goal) => goal.status === 'active');
  const noGoalSelected = activeGoals.length > 0 && !activeGoals.some((goal) => goal.selected);

  // Achievements
  const achievements = useMemo(() => {
    const list: Array<{ icon: typeof Trophy; label: string; unlocked: boolean }> = [
      {
        icon: Fire,
        label: t('teen.progress.achieveStreak3'),
        unlocked: longestStreak >= 3,
      },
      {
        icon: Lightning,
        label: t('teen.progress.achieveStreak7'),
        unlocked: longestStreak >= 7,
      },
      {
        icon: Sword,
        label: t('teen.progress.achieveStreak14'),
        unlocked: longestStreak >= 14,
      },
      {
        icon: Trophy,
        label: t('teen.progress.achieveLevel5'),
        unlocked: level >= 5,
      },
      {
        icon: ShieldStar,
        label: t('teen.progress.achieveLevel10'),
        unlocked: level >= 10,
      },
      {
        icon: Medal,
        label: t('teen.progress.achieveWeekPerfect'),
        unlocked: weekCompletion === 100 && weekStats.total > 0,
      },
    ];
    return list;
  }, [longestStreak, level, weekCompletion, weekStats.total, t]);

  const unlockedCount = achievements.filter((a) => a.unlocked).length;

  // EN kortstil för hela sidan. Tidigare hade varje kort sin egen: tonad glödram,
  // accentkant till vänster, kort i kort, streckade rutor — det var det som gjorde
  // sidan kladdig. Nu skiljer sig korten bara i innehåll.
  const cardSx = {
    bgcolor: tk.card,
    border: `1px solid ${tk.border}`,
    borderRadius: '16px',
    p: '20px',
  } as const;
  const sectionTitleSx = { fontWeight: 700, fontSize: '0.95rem', color: tk.text } as const;

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        // Platt, samma bakgrund som övriga flikar (se TeenHome).
        background: tk.bg,
      }}
    >
      <Container maxWidth="sm" sx={{ pb: 12, px: { xs: 2, sm: 3 } }}>
        <Stack spacing={2.5} pt={2.5}>
          {/* ─── Header ─── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChartLineUp size={22} weight="duotone" color={tk.text} />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.3rem',
                color: tk.text,
                letterSpacing: '-0.03em',
              }}
            >
              {t('teen.progress.title')}
            </Typography>
          </Box>

          {/* ─── Nivå + streak ───
              Ett kort: var jag står (nivå, XP) och hur det går just nu (streak). Streaken
              var ett eget kort med samma tyngd som nivån; som rad under XP-stapeln hör
              den ihop med det den räknar upp. */}
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2.5 }}>
              <Box
                aria-hidden
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  bgcolor: tk.primary,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <TrendUp size={26} weight="bold" color={contrastOn(tk.primary)} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: tk.text,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {t('teen.progress.levelLabel', { level })}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: tk.textSoft }}>
                  {t(levelTitleKey(level))} · {t('teen.progress.totalXP', { xp: totalXP })}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: tk.textSoft }}>
                {t('teen.progress.xpToNext')}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: tk.text }}>
                {xpInLevel}/{XP_PER_LEVEL} XP
              </Typography>
            </Box>
            <Box
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={levelProgress}
              aria-label={t('teen.progress.xpToNext') as string}
              sx={{ height: 10, borderRadius: 5, bgcolor: tk.secondaryBg, overflow: 'hidden' }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${levelProgress}%`,
                  borderRadius: 5,
                  bgcolor: tk.primary,
                  transition: 'width 0.6s ease-out',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              />
            </Box>

            {/* Streak */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 2.5,
                pt: 2,
                borderTop: `1px solid ${tk.border}`,
              }}
            >
              <Fire
                size={24}
                weight={currentStreak > 0 ? 'fill' : 'regular'}
                color={currentStreak > 0 ? tk.primary : tk.textSoft}
                aria-hidden
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: tk.text }}>
                  {currentStreak > 0
                    ? t('teen.progress.streakActive', { count: currentStreak })
                    : t('teen.progress.streakNew')}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: tk.textSoft }}>
                  {currentStreak > 0
                    ? t('teen.progress.streakEncourage')
                    : t('teen.progress.streakFreshStart')}
                </Typography>
              </Box>
              {longestStreak > 0 && (
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: tk.textSoft }}>
                    {t('teen.progress.longestStreak')}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: tk.text }}>
                    {longestStreak} {t('teen.progress.days')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* ─── Butik + Mina köp ───
              Ligger direkt under XP-kortet: det som just räknats upp är också det som
              går att använda, och kopplingen ska synas utan att man letar. Samma två
              villkor som barnvyns StarsPage, av samma skäl — men de hämtas från
              hookarna, inte kopieras: `shopHasItems` för att en ingång till tomma
              hyllor är ett löfte som inte infrias, `hasAnyPurchase` för att en ingång
              som barnet/tonåringen nyss använt aldrig får tas bort igen. Båda hookarna
              startar på false och svarar först när de vet, så raderna dyker upp i
              efterhand i stället för att blinka förbi under fingret.

              RouterLink i stället för useNavigate: det här ÄR länkar till egna vyer, och
              som ankare fungerar långtryck/ny flik och skärmläsaren säger "länk". */}
          {(shopHasItems || hasAnyPurchase) && (
            <Box
              sx={{
                bgcolor: tk.card,
                border: `1px solid ${tk.border}`,
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              {shopHasItems && (
                <ButtonBase
                  component={RouterLink}
                  to="/shop"
                  // Ingen aria-label: den hade ERSATT innehållet som tillgängligt namn och
                  // tystat underrubriken. Rubrik + underrubrik namnger länken bättre själva.
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: '16px 18px',
                    textAlign: 'left',
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      bgcolor: `${tk.primary}1F`,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Storefront size={22} weight="duotone" color={tk.primary} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: tk.text }}>
                      {t('teen.shop.entryTitle')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: tk.textSoft, fontWeight: 500 }}>
                      {t('teen.shop.entryBody')}
                    </Typography>
                  </Box>
                  <CaretRight size={18} weight="bold" color={tk.textSoft} />
                </ButtonBase>
              )}

              {shopHasItems && hasAnyPurchase && (
                <Box sx={{ height: '1px', bgcolor: tk.border, mx: '18px' }} />
              )}

              {hasAnyPurchase && (
                <ButtonBase
                  component={RouterLink}
                  to="/shop/purchases"
                  aria-label={
                    pendingCount > 0
                      ? (t('teen.shop.purchasesEntryAria', { count: pendingCount }) as string)
                      : (t('teen.shop.purchasesEntry') as string)
                  }
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: '16px 18px',
                    textAlign: 'left',
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      bgcolor: `${tk.accent}1F`,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Receipt size={22} weight="duotone" color={tk.accent} />
                  </Box>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: tk.text,
                    }}
                  >
                    {t('teen.shop.purchasesEntry')}
                  </Typography>
                  {/* Siffran är bara det som väntar på en vuxen — en total hade aldrig
                      gått ner och därmed slutat betyda något. aria-label bär samma
                      siffra i ord, så pillret behöver inte läsas upp separat. */}
                  {pendingCount > 0 && (
                    <Box
                      aria-hidden
                      sx={{
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        borderRadius: '999px',
                        bgcolor: tk.accent,
                        color: contrastOn(tk.accent),
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {pendingCount}
                    </Box>
                  )}
                  <CaretRight size={18} weight="bold" color={tk.textSoft} />
                </ButtonBase>
              )}
            </Box>
          )}

          {/* ─── Mål ───
              Föräldern skapar målen, tonåringen bestämmer vart nästa stjärna går. Varje
              rad är en egen tryckyta (ButtonBase) i stället för en knapp inuti raden:
              hela målet är valet, och en större yta är lättare att träffa. Det valda
              målet är INTE tryckbart — det finns inget att välja på det som redan gäller,
              och en död knapp är sämre än ingen knapp. */}
          {activeGoals.length > 0 && (
            <Box
              sx={{
                bgcolor: tk.card,
                border: `1px solid ${tk.border}`,
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: '16px 18px 10px' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Target size={18} weight="duotone" color={tk.primary} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: tk.text }}>
                    {t('teen.progress.goalsTitle')}
                  </Typography>
                </Stack>
                {noGoalSelected && (
                  <Typography
                    sx={{ fontSize: '0.75rem', color: tk.textSoft, fontWeight: 500, mt: 0.5 }}
                  >
                    {t('teen.progress.goalsPickPrompt')}
                  </Typography>
                )}
              </Box>

              {activeGoals.map((goal, index) => {
                const target = Math.max(goal.targetStars, 1);
                const percent = Math.round((Math.min(goal.currentStars, target) / target) * 100);
                const reached = goal.currentStars >= goal.targetStars;
                const selectable = !goal.selected && !reached;

                const row = (
                  <>
                    <Box aria-hidden sx={{ fontSize: '1.3rem', flexShrink: 0 }}>
                      {goal.rewardEmoji ?? '🎁'}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          noWrap
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: tk.text,
                          }}
                        >
                          {goal.rewardName}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: tk.textSoft,
                            flexShrink: 0,
                          }}
                        >
                          {goal.currentStars}/{goal.targetStars}
                        </Typography>
                      </Stack>
                      <Box
                        role="progressbar"
                        aria-valuenow={goal.currentStars}
                        aria-valuemin={0}
                        aria-valuemax={goal.targetStars}
                        aria-label={
                          t('teen.progress.goalProgressAria', {
                            current: goal.currentStars,
                            target: goal.targetStars,
                          }) as string
                        }
                        sx={{
                          mt: 0.75,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: tk.border,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${percent}%`,
                            borderRadius: 3,
                            bgcolor: reached ? tk.primary : tk.accent,
                            transition: 'width 0.4s ease-out',
                            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: reached || goal.selected ? tk.primary : tk.textSoft,
                        }}
                      >
                        {reached
                          ? t('teen.progress.goalReached')
                          : goal.selected
                          ? t('teen.progress.goalCollectingHere')
                          : t('teen.progress.goalSelect')}
                      </Typography>
                    </Box>
                    {selectable && <CaretRight size={18} weight="bold" color={tk.textSoft} />}
                  </>
                );

                const rowSx = {
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '12px 18px',
                  textAlign: 'left' as const,
                  borderTop: index === 0 ? 'none' : `1px solid ${tk.border}`,
                  bgcolor: goal.selected ? `${tk.primary}14` : 'transparent',
                };

                return selectable ? (
                  <ButtonBase
                    key={goal.id}
                    onClick={() => void selectGoal(goal.id)}
                    aria-label={
                      t('teen.progress.goalSelectAria', { reward: goal.rewardName }) as string
                    }
                    sx={rowSx}
                  >
                    {row}
                  </ButtonBase>
                ) : (
                  <Box key={goal.id} sx={rowSx}>
                    {row}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ─── Denna vecka ───
              Siffrorna står direkt i kortet. De låg tidigare i tre egna tonade rutor
              (kort i kort), där mittenrutan dessutom hade en annan färg utan att betyda
              något annat. */}
          <Box sx={cardSx}>
            <Typography sx={{ ...sectionTitleSx, mb: 2 }}>
              {t('teen.progress.weekStats')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[
                { value: weekStats.done, label: t('teen.progress.weekDone') },
                { value: weekStats.total, label: t('teen.progress.weekTotal') },
                { value: `${weekCompletion}%`, label: t('teen.progress.weekRate') },
              ].map((stat, index) => (
                <Box
                  key={stat.label}
                  sx={{
                    textAlign: 'center',
                    borderLeft: index === 0 ? 'none' : `1px solid ${tk.border}`,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 800, fontSize: '1.5rem', color: tk.text, lineHeight: 1.1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: tk.textSoft, mt: 0.5 }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            {weekStats.total > 0 && (
              <Box
                aria-hidden
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: tk.secondaryBg,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${weekCompletion}%`,
                    borderRadius: 4,
                    bgcolor: tk.primary,
                    transition: 'width 0.5s ease-out',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* ─── Vad ger dig energi ───
              Veckans utvärderingar: det som gav energi. Ett kort här i stället för en
              egen statistiksida (FORSLAG-UTVARDERING-OCH-STATISTIK.md, beslut B3). */}
          <TeenEnergyCard cardSx={cardSx} titleSx={sectionTitleSx} />

          {/* ─── Prestationer ───
              Ikoner i ett rutnät, utan en egen ruta runt varje. Upplåst = ifylld ikon i
              temats färg på tonad cirkel; låst = konturikon med streckad cirkel. Samma
              färg för alla upplåsta: sex olika kulörer var mer brus än information. */}
          <Box sx={cardSx}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography sx={sectionTitleSx}>{t('teen.progress.achievements')}</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: tk.textSoft }}>
                {unlockedCount}/{achievements.length}
              </Typography>
            </Box>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: 0,
                p: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                rowGap: 2,
                columnGap: 1,
              }}
            >
              {achievements.map((achievement) => {
                const Icon = achievement.icon;
                return (
                  <Box
                    component="li"
                    key={achievement.label}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: achievement.unlocked ? tk.secondaryBg : 'transparent',
                        border: achievement.unlocked ? 'none' : `1.5px dashed ${tk.border}`,
                      }}
                    >
                      <Icon
                        size={22}
                        weight={achievement.unlocked ? 'fill' : 'regular'}
                        color={achievement.unlocked ? tk.primary : tk.textSoft}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: achievement.unlocked ? tk.text : tk.textSoft,
                        textAlign: 'center',
                        lineHeight: 1.25,
                      }}
                    >
                      {achievement.label}
                      {!achievement.unlocked && (
                        <Box component="span" sx={srOnly}>
                          {` (${t('teen.progress.locked')})`}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Stack>
      </Container>

      <CreateActivityFab onVoiceClick={() => setVoiceOpen(true)} />
      <VoiceActivityCreator
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onSaved={() => setVoiceOpen(false)}
      />
    </Box>
  );
};

export default TeenProgress;
